import React, { useState, useRef, useEffect } from "react";
import {
  Download,
  Upload,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  FileDown,
  Info,
  RefreshCw,
  XCircle,
} from "lucide-react";
import Modal2, { Modal2Footer, Modal2Button } from "./Modal2";

export interface BulkUploadStats {
  total: number;
  success: number;
  failed: number;
}

export interface BulkUploadInlineError {
  row?: number | string;
  field?: string;
  message: string;
}

export interface BulkUploadResultPayload {
  success: boolean;
  message: string;
  stats?: BulkUploadStats;
  duplicates?: string[];
  inlineErrors?: BulkUploadInlineError[];
  errorFileAction?: {
    label?: string;
    description?: string;
    onDownload: () => void;
  };
}

export interface GenericBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;

  /** File validation and info */
  acceptedExtensions: string[]; // e.g. ['.xlsx'] or ['.csv']
  fileTypeLabel?: string; // e.g. "Excel (.xlsx)" or "CSV (.csv)"

  /** Template download configuration */
  templateConfig: {
    title?: string;
    description?: string;
    buttonLabel?: string;
    isDownloading?: boolean;
    onDownload: () => void | Promise<void>;
  };

  /** Upload action */
  isUploading: boolean;
  onUpload: (file: File) => Promise<void> | void;

  /** Notes & guidelines */
  instructions?: string[];

  /** Upload result (if passed, modal renders the results view) */
  result?: BulkUploadResultPayload | null;
  onResetResult?: () => void;
  onSuccessClose?: () => void;

  /** Custom width if needed (defaults to 720) */
  width?: number | string;
}

export const GenericBulkUploadModal: React.FC<GenericBulkUploadModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  acceptedExtensions,
  fileTypeLabel = "Excel (.xlsx)",
  templateConfig,
  isUploading,
  onUpload,
  instructions = [],
  result,
  onResetResult,
  onSuccessClose,
  width = 720,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setDragActive(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isOpen]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateAndUpload = (file: File) => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const isValidExt = acceptedExtensions.some(
      (expected) => expected.toLowerCase() === ext
    );

    if (!isValidExt) {
      alert(`Invalid file type. Please upload a file with format: ${acceptedExtensions.join(", ")}`);
      return;
    }

    onUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (isUploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndUpload(e.target.files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (result && onSuccessClose) {
      onSuccessClose();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal2
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      width={width}
      footer={
        result ? (
          <Modal2Footer justify="between" className="w-full flex-col-reverse sm:flex-row gap-2">
            <div>
              {onResetResult && (
                <Modal2Button
                  variant="ghost"
                  onClick={() => {
                    onResetResult();
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className="w-full sm:w-auto text-xs sm:text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Upload Another File
                </Modal2Button>
              )}
            </div>
            <Modal2Button variant="primary" onClick={handleClose} className="w-full sm:w-auto">
              Done
            </Modal2Button>
          </Modal2Footer>
        ) : undefined
      }
    >
      {!result ? (
        <div className="space-y-4">
          {/* Template Download Card */}
          <div className="bg-[var(--color-primary-50)] border border-[var(--color-primary-200)] rounded-[13px] p-3.5 sm:p-4 transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5">
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={templateConfig.onDownload}
                  disabled={templateConfig.isDownloading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-[13px] bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold text-xs sm:text-sm hover:opacity-95 transition-all shadow-[var(--shadow-brand)] disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                >
                  {templateConfig.isDownloading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-white" />
                  )}
                  <span>
                    {templateConfig.isDownloading
                      ? "Downloading..."
                      : templateConfig.buttonLabel || "Download Template"}
                  </span>
                </button>
                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
                  {templateConfig.description ||
                    "Excel template with headers, example row, and reference tables of allowed values."}
                </p>
              </div>
            </div>
          </div>

          {/* Upload Dropzone Area */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Upload Excel File
              </h3>
            </div>

            <div
              className={`relative border-2 border-dashed rounded-[13px] p-6 sm:p-8 text-center transition-all cursor-pointer ${
                isUploading
                  ? "border-[var(--color-primary-400)] bg-[var(--color-primary-50)]"
                  : dragActive
                  ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)] shadow-inner"
                  : "border-[var(--color-border-input)] bg-[var(--color-bg-base)] hover:border-[var(--color-primary-200)] hover:bg-[var(--color-primary-50)]"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={triggerFileInput}
            >
              {isUploading ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="relative mb-3">
                    <div className="w-10 h-10 border-3 border-[var(--color-primary-200)] border-t-[var(--color-primary-600)] rounded-full animate-spin" />
                    <Upload className="w-4 h-4 text-[var(--color-primary-600)] absolute inset-0 m-auto" />
                  </div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Uploading & Validating Data...
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    Please do not close this window while processing.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-2">
                  <div className="w-10 h-10 rounded-[13px] bg-[var(--color-bg-muted)] shadow-xs flex items-center justify-center text-[var(--color-primary-600)] border border-[var(--color-border-primary)] mb-2.5">
                    <Upload className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Drop your Excel file here
                  </p>
                  <p className="text-xs text-[var(--color-text-tertiary)] mt-1 mb-3">
                    or click to browse files ({acceptedExtensions.join(", ")})
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFileInput();
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium text-xs rounded-[13px] shadow-[var(--shadow-brand)] hover:opacity-95 transition-all"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Choose File</span>
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedExtensions.join(",")}
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
            </div>
          </div>

          {/* Guidelines */}
          {instructions.length > 0 && (
            <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)]/30 rounded-[13px] p-3.5">
              <div className="flex items-start gap-2.5">
                <Info className="w-4 h-4 text-[var(--color-warning)] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-semibold text-[var(--color-text-primary)] uppercase tracking-wider mb-1.5">
                    Important Notes
                  </h4>
                  <ul className="text-xs text-[var(--color-text-strong)] space-y-1">
                    {instructions.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-1.5">
                        <span className="text-[var(--color-warning)] font-bold">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Results View */
        <div className="space-y-4">
          {/* Header Status Banner */}
          <div
            className={`flex items-start gap-3 p-3.5 rounded-lg border ${
              !result.success
                ? "bg-[var(--color-error-light)] border-[var(--color-error)]/30 text-[var(--color-error)]"
                : result.stats && result.stats.failed > 0
                ? "bg-[var(--color-warning-light)] border-[var(--color-warning)]/30 text-[var(--color-text-primary)]"
                : "bg-[var(--color-success-light)] border-[var(--color-success)]/30 text-[var(--color-text-primary)]"
            }`}
          >
            <div className="mt-0.5 flex-shrink-0">
              {!result.success ? (
                <XCircle className="w-5 h-5 text-[var(--color-error)]" />
              ) : result.stats && result.stats.failed > 0 ? (
                <AlertCircle className="w-5 h-5 text-[var(--color-warning)]" />
              ) : (
                <CheckCircle className="w-5 h-5 text-[var(--color-success)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold">
                {!result.success
                  ? "Upload Process Failed"
                  : result.stats && result.stats.failed > 0
                  ? "Upload Completed with Some Issues"
                  : "Upload Processed Successfully"}
              </h3>
              <p className="text-xs mt-0.5 leading-relaxed text-[var(--color-text-secondary)]">
                {result.message}
              </p>
            </div>
          </div>

          {/* Metric Stats Cards */}
          {result.stats && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
              <div className="bg-[var(--color-bg-muted)] border border-[var(--color-border-primary)] rounded-lg p-3 text-center">
                <div className="text-lg sm:text-xl font-bold text-[var(--color-text-primary)]">
                  {result.stats.total}
                </div>
                <div className="text-[11px] font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider mt-0.5">
                  Total Records
                </div>
              </div>

              <div className="bg-[var(--color-success-light)] border border-[var(--color-success)]/20 rounded-lg p-3 text-center">
                <div className="text-lg sm:text-xl font-bold text-[var(--color-success)]">
                  {result.stats.success}
                </div>
                <div className="text-[11px] font-semibold text-[var(--color-success)]/80 uppercase tracking-wider mt-0.5">
                  Imported
                </div>
              </div>

              <div className="bg-[var(--color-error-light)] border border-[var(--color-error)]/20 rounded-lg p-3 text-center">
                <div className="text-lg sm:text-xl font-bold text-[var(--color-error)]">
                  {result.stats.failed}
                </div>
                <div className="text-[11px] font-semibold text-[var(--color-error)]/80 uppercase tracking-wider mt-0.5">
                  Failed
                </div>
              </div>
            </div>
          )}

          {/* Error File Download Card */}
          {result.errorFileAction && (
            <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)]/30 rounded-lg p-3.5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <FileDown className="w-4.5 h-4.5 text-[var(--color-warning)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-xs font-semibold text-[var(--color-text-primary)] mb-0.5">
                      {result.errorFileAction.label || "Download Detailed Error Report"}
                    </h4>
                    <p className="text-xs text-[var(--color-text-strong)] leading-relaxed">
                      {result.errorFileAction.description ||
                        "An Excel sheet with failed rows and error annotations is available. Fix the highlighted errors and re-upload the file."}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={result.errorFileAction.onDownload}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--color-bg-base)] border border-[var(--color-warning)]/40 text-[var(--color-text-primary)] font-semibold text-xs hover:bg-[var(--color-warning-light)] transition-colors shadow-xs flex-shrink-0 w-full sm:w-auto"
                >
                  <Download className="w-3.5 h-3.5 text-[var(--color-warning)]" />
                  <span>Download Error File</span>
                </button>
              </div>
            </div>
          )}

          {/* Duplicates Inline List */}
          {result.duplicates && result.duplicates.length > 0 && (
            <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)]/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold text-[var(--color-text-primary)]">
                  Duplicate Records Found ({result.duplicates.length})
                </h4>
              </div>
              <div className="bg-[var(--color-bg-base)] border border-[var(--color-warning)]/20 rounded-md p-2 max-h-32 overflow-y-auto divide-y divide-[var(--color-border-primary)] text-xs text-[var(--color-text-strong)]">
                {result.duplicates.map((dup, i) => (
                  <div key={i} className="py-1 px-1">
                    {dup}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inline Validation Errors List */}
          {result.inlineErrors && result.inlineErrors.length > 0 && (
            <div className="bg-[var(--color-error-light)] border border-[var(--color-error)]/30 rounded-lg p-3 space-y-2">
              <h4 className="text-xs font-semibold text-[var(--color-error)]">
                Validation Errors ({result.inlineErrors.length})
              </h4>
              <div className="bg-[var(--color-bg-base)] border border-[var(--color-error)]/20 rounded-md p-2 max-h-40 overflow-y-auto divide-y divide-[var(--color-error-light)] text-xs text-[var(--color-text-primary)]">
                {result.inlineErrors.map((err, i) => (
                  <div key={i} className="py-1.5 px-1 flex items-start gap-2">
                    <span className="font-semibold text-[var(--color-error)]">
                      {err.row ? `Row ${err.row}:` : "•"}
                    </span>
                    <span>
                      {err.field ? <strong className="text-[var(--color-text-primary)]">{err.field} - </strong> : null}
                      {err.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal2>
  );
};

export default GenericBulkUploadModal;
